import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import {
  OUT_OF_OFFICE_VARS,
  countVarUsage,
  getVarPlaceholderLabel,
  getVarToken,
  resolveOutOfOfficeVars,
  splitOutOfOfficeVars,
} from '../helpers/outOfOffice';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';

// --- serialisation -------------------------------------------------------
// The editable area shows a placeholder as the date it resolves to, but the
// value handed back to the form always contains the raw `%%key%%` token. Chips
// are atomic (contenteditable="false"), so they can only be removed as a whole
// and can never end up half deleted.

// A caret cannot sit between two adjacent non-editable chips — there is no text
// node for it to occupy, so the browser hides it or paints it inside the chip.
// Every chip therefore gets a zero width space on each side to live in. It is
// invisible, and stripped again on the way out.
const ZWSP = '\u200B';
const ZWSP_RE = /\u200B/g;

function isChip(node) {
  return node?.nodeType === Node.ELEMENT_NODE && Boolean(node.dataset?.var);
}

// A text node holding nothing but caret padding.
function isPadding(node) {
  return (
    node?.nodeType === Node.TEXT_NODE &&
    node.textContent.replace(ZWSP_RE, '') === ''
  );
}

function serialize(root) {
  let out = '';

  const walk = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent.replace(ZWSP_RE, '');
        return;
      }
      if (child.nodeName === 'BR') {
        out += '\n';
        return;
      }
      const varKey = child.dataset?.var;
      if (varKey) {
        // The chip's visible text is a date or `[Start date]` — what goes back
        // to the form (and to the API) is always the raw token.
        out += getVarToken(varKey);
        return;
      }
      // Unwrap anything the browser produced on its own (pasted markup, ...).
      walk(child);
    });
  };

  walk(root);

  // A <br> as the very last node is swallowed by the browser, so one is kept
  // around purely to make the caret visible on a fresh last line. It is layout,
  // not content — drop it again here.
  if (root.lastChild?.nodeName === 'BR' && out.endsWith('\n'))
    out = out.slice(0, -1);

  return out;
}

function createChip(key, label, isResolved) {
  const chip = document.createElement('span');
  chip.dataset.var = key;
  chip.contentEditable = 'false';
  chip.className = 'ooo-chip' + (isResolved ? '' : ' ooo-chip-pending');
  chip.textContent = label;
  return chip;
}

function createTextFragment(text) {
  const fragment = document.createDocumentFragment();
  text.split('\n').forEach((line, index) => {
    if (index > 0) fragment.appendChild(document.createElement('br'));
    if (line) fragment.appendChild(document.createTextNode(line));
  });
  return fragment;
}

// Appends a chip together with the padding that keeps the caret placeable on
// either side of it. The leading pad is skipped when a text node already
// precedes the chip.
function appendChip(parent, chip) {
  if (parent.lastChild?.nodeType !== Node.TEXT_NODE)
    parent.appendChild(document.createTextNode(ZWSP));
  parent.appendChild(chip);
  parent.appendChild(document.createTextNode(ZWSP));
}

function buildContent(value, varValues, dynamicDates) {
  const fragment = document.createDocumentFragment();

  splitOutOfOfficeVars(value, varValues).forEach((part) => {
    if (part.type === 'var') {
      appendChip(
        fragment,
        createChip(part.key, part.value, part.resolved || dynamicDates),
      );
      return;
    }
    fragment.appendChild(createTextFragment(part.value));
  });

  // Counterpart to the trailing <br> that serialize() drops, so value → DOM →
  // value round trips unchanged.
  if (fragment.lastChild?.nodeName === 'BR')
    fragment.appendChild(document.createElement('br'));

  return fragment;
}

/**
 * Text field that renders `%%startDate%%` / `%%endDate%%` as inline chips
 * showing the actual date — the same wording the recipient will read — while
 * still storing the placeholders. The chips next to the label insert a
 * placeholder at the caret.
 */
export const VariableTextField = ({
  label,
  value = '',
  onChange,
  placeholder,
  multiline,
  minRows = 6,
  varValues = {},
  // The dates are filled in later (vacation mode) — an unresolved placeholder
  // is expected here, so it must not be flagged as something missing.
  dynamicDates,
  error,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const editorRef = useRef(null);
  // Read inside DOM callbacks — keeping it out of the effect deps stops the
  // editor from being rebuilt (and the caret from jumping) on a date change.
  const varValuesRef = useRef(varValues);
  varValuesRef.current = varValues;
  const dynamicDatesRef = useRef(dynamicDates);
  dynamicDatesRef.current = dynamicDates;
  // The caret is lost when a chip button takes focus, so the last range inside
  // the editor is remembered.
  const savedRangeRef = useRef(null);

  // Rebuild only when the value changed from the outside (mount, template,
  // reset). Typing already emitted this exact value, so this is a no-op then.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || serialize(editor) === value) return;

    editor.innerHTML = '';
    editor.appendChild(
      buildContent(value, varValuesRef.current, dynamicDatesRef.current),
    );
  }, [value]);

  // A changed date only relabels the existing chips — the value stays the same.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.querySelectorAll('[data-var]').forEach((chip) => {
      const resolved = varValues[chip.dataset.var];
      const nextLabel = resolved || getVarPlaceholderLabel(chip.dataset.var);
      if (chip.textContent !== nextLabel) chip.textContent = nextLabel;
      chip.classList.toggle('ooo-chip-pending', !resolved && !dynamicDates);
    });
  }, [varValues, dynamicDates]);

  function emitChange() {
    if (editorRef.current) onChange(serialize(editorRef.current));
  }

  function saveSelection() {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current?.contains(range.commonAncestorContainer))
      savedRangeRef.current = range.cloneRange();
  }

  function setCaret(range) {
    savedRangeRef.current = range.cloneRange();
    editorRef.current?.focus();
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    selection.addRange(range);
  }

  function caretInside(node, offset) {
    const range = document.createRange();
    range.setStart(node, offset);
    range.collapse(true);
    setCaret(range);
  }

  // Drops content at the remembered caret, leaves the caret behind it and
  // returns the last inserted node. `caretTarget` overrides where the caret
  // ends up — used to park it inside a chip's trailing padding.
  function insertAtCaret(node, caretTarget) {
    const editor = editorRef.current;
    if (!editor) return null;

    let range = savedRangeRef.current;
    if (!range || !editor.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }

    range.deleteContents();
    // A fragment is emptied by insertNode, so the anchor for the caret has to
    // be picked beforehand. For a chip the anchor is the chip itself — never
    // its inner text node, which sits inside a non-editable element.
    const lastNode =
      node.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? node.lastChild : node;
    range.insertNode(node);
    if (!lastNode) return null;

    if (caretTarget) {
      caretInside(caretTarget, caretTarget.length);
    } else {
      const nextRange = document.createRange();
      nextRange.setStartAfter(lastNode);
      nextRange.collapse(true);
      setCaret(nextRange);
    }

    emitChange();
    return lastNode;
  }

  function handleInsertVar(variable) {
    saveSelection();
    const resolved = varValues[variable.key];
    const chip = createChip(
      variable.key,
      resolved || getVarPlaceholderLabel(variable.key),
      Boolean(resolved) || Boolean(dynamicDates),
    );

    // The chip travels with its trailing pad, and the caret lands inside that
    // pad — so it stays visible even when the next thing inserted is a chip.
    const fragment = document.createDocumentFragment();
    fragment.appendChild(chip);
    const tail = document.createTextNode(ZWSP);
    fragment.appendChild(tail);

    insertAtCaret(fragment, tail);

    // Nothing to the left to hold a caret either — give it a pad.
    if (chip.previousSibling?.nodeType !== Node.TEXT_NODE)
      chip.parentNode.insertBefore(document.createTextNode(ZWSP), chip);
  }

  /**
   * Backspace / Delete next to a chip. Without this the caret padding would
   * absorb the first keypress, so the chip would only disappear on the second —
   * and each pad would then need its own press. Removes chip and padding in one
   * go, leaving a single pad behind for the caret.
   */
  function removeAdjacentChip(event, forward) {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return false;

    const range = selection.getRangeAt(0);
    // A real selection is the browser's job.
    if (!range.collapsed) return false;

    const { startContainer: container, startOffset: offset } = range;
    let node;

    if (container.nodeType === Node.TEXT_NODE) {
      const side = forward
        ? container.textContent.slice(offset)
        : container.textContent.slice(0, offset);
      // Real characters on that side — normal editing, hands off.
      if (side.replace(ZWSP_RE, '') !== '') return false;
      node = forward ? container.nextSibling : container.previousSibling;
    } else {
      node = container.childNodes[forward ? offset : offset - 1];
    }

    // Step over the invisible padding to find out what is really there.
    while (isPadding(node))
      node = forward ? node.nextSibling : node.previousSibling;

    if (!isChip(node)) return false;

    // The chip goes together with the padding on both sides, so no orphaned
    // zero width nodes are left to absorb the next keypress.
    const group = [node];
    let scan = node.previousSibling;
    while (isPadding(scan)) {
      group.unshift(scan);
      scan = scan.previousSibling;
    }
    const left = scan;

    scan = node.nextSibling;
    while (isPadding(scan)) {
      group.push(scan);
      scan = scan.nextSibling;
    }
    const right = scan;

    event.preventDefault();

    // Only re-pad when neither neighbour can hold the caret — between two
    // chips, at the very start, or next to a line break.
    const needsPad =
      left?.nodeType !== Node.TEXT_NODE && right?.nodeType !== Node.TEXT_NODE;
    const pad = needsPad ? document.createTextNode(ZWSP) : null;
    if (pad) node.parentNode.insertBefore(pad, node);

    group.forEach((entry) => entry.remove());

    if (pad) caretInside(pad, pad.length);
    else if (left?.nodeType === Node.TEXT_NODE)
      caretInside(left, left.textContent.length);
    else caretInside(right, 0);

    emitChange();
    return true;
  }

  function handleKeyDown(event) {
    if (event.key === 'Backspace') return removeAdjacentChip(event, false);
    if (event.key === 'Delete') return removeAdjacentChip(event, true);
    if (event.key !== 'Enter') return;
    // Enter is handled here so the browser cannot wrap lines in <div>s.
    event.preventDefault();
    // A single line subject never breaks.
    if (!multiline) return;

    saveSelection();
    const inserted = insertAtCaret(document.createElement('br'));

    // Only a break at the very end needs the extra filler — adding it mid text
    // would produce two empty lines instead of one.
    const editor = editorRef.current;
    if (inserted && editor?.lastChild === inserted)
      editor.appendChild(document.createElement('br'));
  }

  function handlePaste(event) {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    if (!text) return;
    saveSelection();
    insertAtCaret(
      createTextFragment(multiline ? text : text.replace(/[\r\n]+/g, ' ')),
    );
  }

  const isEmpty = !value;

  return (
    <Box className="flex flex-col gap-1.5">
      <Box className="flex flex-wrap items-center justify-between gap-2">
        <Typography
          fontSize={12}
          fontWeight={600}
          sx={{ color: error ? colors.error : undefined }}
          className={error ? '' : 'opacity-70'}
        >
          {label}
        </Typography>
        <Box className="flex flex-wrap items-center gap-1.5">
          <Typography fontSize={11} className="opacity-50">
            {t('Insert')}
          </Typography>
          {OUT_OF_OFFICE_VARS.map((variable) => (
            <VarButton
              key={variable.key}
              variable={variable}
              resolvedValue={varValues[variable.key]}
              dynamicDates={dynamicDates}
              usageCount={countVarUsage(variable.key, value)}
              onClick={() => handleInsertVar(variable)}
              colors={colors}
            />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          borderRadius: '10px',
          bgcolor: colors.bgInput,
          border: `1px solid ${error ? colors.error : colors.selected}`,
          transition: 'border-color 0.2s',
          ':hover': {
            borderColor: error ? colors.error : colors.info + '80',
          },
          ':focus-within': {
            borderColor: error ? colors.error : colors.info,
          },
          '& .ooo-editor': {
            outline: 'none',
            padding: '11px 13px',
            fontSize: '14px',
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            minHeight: multiline ? `${minRows * 1.7}em` : '1.7em',
            maxHeight: multiline ? '260px' : 'auto',
            overflowY: multiline ? 'auto' : 'hidden',
          },
          '& .ooo-chip': {
            display: 'inline-block',
            bgcolor: colors.info + '30',
            border: `1px solid ${colors.info}60`,
            borderRadius: '5px',
            padding: '0px 6px',
            margin: '0px 1px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          },
          '& .ooo-chip-pending': {
            bgcolor: colors.warning + '30',
            border: `1px solid ${colors.warning}60`,
            color: colors.warning,
          },
        }}
      >
        {isEmpty && placeholder && (
          <Typography
            fontSize={14}
            className="absolute opacity-40 pointer-events-none"
            sx={{ top: '11px', left: '13px', lineHeight: 1.7 }}
          >
            {/* Shows the example with real dates once a period is picked. */}
            {resolveOutOfOfficeVars(placeholder, varValues)}
          </Typography>
        )}
        <Box
          component="div"
          ref={editorRef}
          className="ooo-editor"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label={label}
          aria-multiline={Boolean(multiline)}
          onInput={() => {
            saveSelection();
            emitChange();
          }}
          onKeyDown={handleKeyDown}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          onBlur={saveSelection}
          onPaste={handlePaste}
          onDrop={(event) => event.preventDefault()}
        />
      </Box>

      {error && (
        <Typography fontSize={11} sx={{ color: colors.error }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

const VarButton = ({
  variable,
  resolvedValue,
  dynamicDates,
  usageCount,
  onClick,
  colors,
}) => {
  const { t } = useTranslation();
  const isUsed = usageCount > 0;

  return (
    <Tooltip
      title={
        resolvedValue
          ? `${getVarPlaceholderLabel(variable.key)} → ${resolvedValue}`
          : dynamicDates
            ? t('Filled in from the vacation period when the reply is sent')
            : t('Pick a date to see the resolved value')
      }
    >
      <Box
        component="button"
        type="button"
        // Keep the caret where it is: focus must not leave the editor.
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '3px 10px 3px 6px',
          borderRadius: '9999px',
          cursor: 'pointer',
          fontSize: '11px',
          fontWeight: 600,
          transition: '0.2s ease',
          bgcolor: isUsed ? colors.info + '30' : colors.card,
          border: `1px solid ${isUsed ? colors.info + '80' : 'transparent'}`,
          color: 'inherit',
          ':hover': {
            bgcolor: colors.info + '40',
            border: `1px solid ${colors.info}`,
          },
        }}
      >
        {isUsed ? (
          <CheckIcon sx={{ fontSize: '14px', color: colors.info }} />
        ) : (
          <AddIcon sx={{ fontSize: '14px' }} className="opacity-60" />
        )}
        {t(variable.label)}
        {usageCount > 1 && (
          <Box
            component="span"
            sx={{ color: colors.info }}
            className="opacity-80"
          >
            ×{usageCount}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};
